import React, { useEffect, useState, useMemo } from 'react';
import ComponentWrapper from './ComponentWrapper';

/**
 * Minimal re-render time component - only updates when time data changes
 * @param {Object} props - Component props
 * @param {React.Component} props.Component - Component to render
 * @param {Object} props.componentProps - Props for the component
 * @param {string} props.componentName - Component name
 * @param {string} props.displayName - Component display name
 * @returns {JSX.Element} Time component
 */
const MinimalTimeComponent = React.memo(({ Component, componentProps, componentName, displayName }) => {
  const [timeData, setTimeData] = useState(() => {
    const now = new Date();
    return {
      time: now.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' }),
      date: now.toLocaleDateString('en-US', { weekday: 'short', day: '2-digit', month: 'short' }),
      weather: { condition: "Thunderstorm", temperature: 26, icon: "🌩️" },
      _timestamp: now.getTime()
    };
  });
  
  // Update only time data, not the entire component
  useEffect(() => {
    const timer = setInterval(() => {
      const newTime = new Date();
      const timeString = newTime.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' });
      const dateString = newTime.toLocaleDateString('en-US', { weekday: 'short', day: '2-digit', month: 'short' });
      
      // Only update if time actually changed (prevents unnecessary updates)
      setTimeData(prevData => {
        if (prevData.time === timeString && prevData.date === dateString) {
          return prevData; // No change, prevent re-render
        }
        
        return {
          time: timeString,
          date: dateString,
          weather: { condition: "Thunderstorm", temperature: 26, icon: "🌩️" },
          _timestamp: newTime.getTime()
        };
      });
    }, 5000);
    
    return () => clearInterval(timer);
  }, [displayName]);
  
  // Memoize props to prevent unnecessary re-renders
  const finalProps = useMemo(() => ({
    ...componentProps,
    data: timeData
  }), [componentProps, timeData]);
  
  return (
    <ComponentWrapper>
      <Component 
        {...finalProps}
        key={componentName} // Static key
      />
    </ComponentWrapper>
  );
});

MinimalTimeComponent.displayName = 'MinimalTimeComponent';

export default MinimalTimeComponent;
